<template>
  <v-dialog v-model="dialogVisible" max-width="980" persistent>
    <v-card>
      <v-toolbar color="white" :title="title">
        <v-btn icon="mdi-close" @click="closeDialog"></v-btn>
      </v-toolbar>
      <v-card-text class="cropper-dialog-body">
        <p class="cropper-hint">Kasuta hiirt või puuteekraani pildi liigutamiseks. Nuppudega saad zoomida.</p>
        <div class="cropper-shell">
          <Cropper
              ref="cropper"
              :src="src"
              :stencil-props="stencilProps"
              :auto-zoom="true"
              :canvas="true"
              :cross-origin="cropperCrossOrigin"
              :transitions="false"
              image-class="cropper-image"
              @change="onCropChange"
          />
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="cropper-actions pa-4">
        <div class="d-flex flex-wrap gap-2">
          <v-btn variant="outlined" icon="mdi-magnify-plus-outline" @click="zoomIn"></v-btn>
          <v-btn variant="outlined" icon="mdi-magnify-minus-outline" @click="zoomOut"></v-btn>
          <v-btn variant="outlined" icon="mdi-refresh" @click="resetCropper"></v-btn>
          <div v-if="inputWidth" class="cropper-dimensions">
            <input
                v-model.number="inputWidth"
                class="cropper-dim-input"
                type="number"
                min="1"
                @change="applyCropWidth"
                @keydown.enter.prevent="applyCropWidth"
            />
            <span class="cropper-dim-sep">&times;</span>
            <input
                v-model.number="inputHeight"
                class="cropper-dim-input"
                type="number"
                min="1"
                @change="applyCropHeight"
                @keydown.enter.prevent="applyCropHeight"
            />
            <span class="cropper-dim-label">px</span>
          </div>
        </div>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="closeDialog">Tühista</v-btn>
        <v-btn color="success" :loading="saving" @click="saveCrop">Kasuta pilti</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { Cropper } from 'vue-advanced-cropper';
import 'vue-advanced-cropper/dist/style.css';

export default {
  name: 'ImageCropperDialog',
  components: {
    Cropper,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    src: {
      type: String,
      default: '',
    },
    aspectRatio: {
      type: Number,
      default: null,
    },
    title: {
      type: String,
      default: 'Kärbi pilti',
    },
    outputMimeType: {
      type: String,
      default: 'image/jpeg',
    },
    outputQuality: {
      type: Number,
      default: 0.92,
    },
    outputWidth: {
      type: Number,
      default: null,
    },
    outputHeight: {
      type: Number,
      default: null,
    },
    outputName: {
      type: String,
      default: 'cropped-image.jpg',
    },
  },
  emits: ['update:modelValue', 'confirm', 'cancel'],
  data() {
    return {
      saving: false,
      errorMessage: '',
      cropCoordinates: null,
      inputWidth: null,
      inputHeight: null,
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },
    stencilProps() {
      const props = {};
      if (this.aspectRatio !== null && this.aspectRatio !== undefined) {
        props.aspectRatio = this.aspectRatio;
      }
      return props;
    },
    cropperCrossOrigin() {
      if (!this.src) return undefined;

      if (this.src.startsWith('blob:') || this.src.startsWith('data:')) {
        return undefined;
      }

      return undefined;
    },
  },
  watch: {
    dialogVisible(isVisible) {
      if (isVisible) {
        this.errorMessage = '';
        this.$nextTick(() => {
          this.$refs.cropper?.refresh?.();
        });
      }
    },
    src() {
      if (this.dialogVisible) {
        this.$nextTick(() => {
          this.$refs.cropper?.refresh?.();
        });
      }
    },
  },
  methods: {
    onCropChange({ coordinates }) {
      this.cropCoordinates = coordinates;
      this.inputWidth = Math.round(coordinates.width);
      this.inputHeight = Math.round(coordinates.height);
    },
    applyCropWidth() {
      const w = parseInt(this.inputWidth);
      if (!w || w < 1) return;
      this.$refs.cropper?.setCoordinates?.({ width: w });
    },
    applyCropHeight() {
      const h = parseInt(this.inputHeight);
      if (!h || h < 1) return;
      this.$refs.cropper?.setCoordinates?.({ height: h });
    },
    closeDialog() {
      if (this.saving) return;
      this.$emit('cancel');
      this.$emit('update:modelValue', false);
    },
    zoomIn() {
      this.$refs.cropper?.zoom?.(1.1);
    },
    zoomOut() {
      this.$refs.cropper?.zoom?.(0.9);
    },
    resetCropper() {
      this.$refs.cropper?.reset?.();
    },
    createOutputCanvas(sourceCanvas) {
      const srcW = sourceCanvas.width;
      const srcH = sourceCanvas.height;

      let outW, outH;
      if (this.outputWidth && this.outputHeight) {
        outW = this.outputWidth;
        outH = this.outputHeight;
      } else if (this.outputWidth) {
        outW = this.outputWidth;
        outH = Math.round(srcH * (this.outputWidth / srcW));
      } else {
        const maxW = 1600;
        if (srcW > maxW) {
          outW = maxW;
          outH = Math.round(srcH * (maxW / srcW));
        } else {
          outW = srcW;
          outH = srcH;
        }
      }

      const outputCanvas = document.createElement('canvas');
      outputCanvas.width = outW;
      outputCanvas.height = outH;

      const context = outputCanvas.getContext('2d');
      if (!context) return null;

      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      context.drawImage(sourceCanvas, 0, 0, srcW, srcH, 0, 0, outW, outH);

      return outputCanvas;
    },
    async saveCrop() {
      const cropper = this.$refs.cropper;
      const result = cropper?.getResult?.();
      const canvas = result?.canvas;

      if (!canvas) {
        this.errorMessage = 'Kärpitud pilti ei õnnestunud luua';
        return;
      }

      this.saving = true;
      this.errorMessage = '';

      try {
        const fixedCanvas = this.createOutputCanvas(canvas);
        if (!fixedCanvas) {
          throw new Error('Failed to create output canvas');
        }

        const dataUrl = fixedCanvas.toDataURL(this.outputMimeType, this.outputQuality);
        if (!dataUrl) {
          throw new Error('Cropped image is empty');
        }

        this.$emit('confirm', {
          dataUrl,
          name: this.outputName,
        });
      } catch (error) {
        this.errorMessage = 'Kärpitud pildi salvestamine ebaõnnestus';
        console.log(error);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.cropper-dialog-body {
  padding: 20px;
}

.cropper-hint {
  margin: 0 0 12px 0;
  color: #5d6b7a;
  font-size: 13px;
}

.cropper-shell {
  width: 100%;
  height: min(68vh, 620px);
  border-radius: 12px;
  overflow: hidden;
  background: #f3f6fb;
}

.cropper-shell :deep(.cropper) {
  width: 100%;
  height: 100%;
  background: #f3f6fb;
}

.cropper-shell :deep(.cropper-image) {
  cursor: grab;
}

.cropper-shell :deep(.cropper-image:active) {
  cursor: grabbing;
}

.cropper-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

:deep(.gap-2) {
  gap: 8px;
}

.cropper-dimensions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.cropper-dim-input {
  width: 72px;
  padding: 4px 6px;
  border: 1px solid #c8d0da;
  border-radius: 6px;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  text-align: center;
  color: #2c3e50;
  background: #fff;
  outline: none;
  transition: border-color 0.15s;
}

.cropper-dim-input:focus {
  border-color: #1976d2;
}

.cropper-dim-input::-webkit-inner-spin-button,
.cropper-dim-input::-webkit-outer-spin-button {
  opacity: 1;
}

.cropper-dim-sep {
  font-size: 14px;
  color: #5d6b7a;
}

.cropper-dim-label {
  font-size: 13px;
  color: #5d6b7a;
  margin-left: 2px;
}
</style>
